import {
    DEFAULT_CHARACTER_ID_KEY,
    forgetStoredValue,
    getStoredValue,
    SELECTED_CHARACTER_ID_KEY,
    storeValue,
} from '@sorrem-client/ui/shared/browser-storage';
import { getStoredCanonicalAccountId } from '@sorrem-client/ui/shared/auth-session';
import { createValueStore, type ValueStore } from '@sorrem-client/ui/shared/value-store';
import { useValueStoreSelector } from './value-store';

/**
 * Where this tab's account keeps its default character. Kept per account:
 * localStorage is shared by every window of the browser, and a window signed
 * in as another account must not start on (and act as) this one's character.
 * Null when signed out.
 */
function resolveDefaultCharacterIdKey(): string | null {
    const accountId = getStoredCanonicalAccountId();
    return accountId !== null ? `${DEFAULT_CHARACTER_ID_KEY}:${accountId}` : null;
}

/**
 * Reads this tab's selected character id directly from storage, for
 * non-component code (module-level socket stores) that can't call the
 * `useSelectedCharacterId` hook. Mirrors the hook's own fallback order:
 * this tab's sessionStorage choice, else the cross-tab default.
 */
export function getStoredSelectedCharacterId(): string | null {
    const defaultKey = resolveDefaultCharacterIdKey();
    return getStoredValue('session', SELECTED_CHARACTER_ID_KEY)
        ?? (defaultKey !== null ? getStoredValue('local', defaultKey) : undefined)
        ?? null;
}

// Module-level store so every `useSelectedCharacterId` caller shares one
// selection: picking a character in one component (e.g. the character
// selector) re-renders every other component reading the selection.
let selectionStore: ValueStore<string | null> | undefined;

function getSelectionStore(): ValueStore<string | null> {
    // Created on first use so importing this module never touches storage.
    selectionStore ??= createValueStore(getStoredSelectedCharacterId());
    return selectionStore;
}

function setSelectedCharacterId(characterId: string | null): void {
    const store = getSelectionStore();
    if (store.get() === characterId) {
        return;
    }

    const defaultKey = resolveDefaultCharacterIdKey();
    if (characterId === null) {
        forgetStoredValue('session', SELECTED_CHARACTER_ID_KEY);
        if (defaultKey !== null) {
            forgetStoredValue('local', defaultKey);
        }
    } else {
        // This tab's selection (independent per tab)...
        storeValue('session', SELECTED_CHARACTER_ID_KEY, characterId);
        // ...and the account's default for future sessions (all tabs, across browser restarts).
        if (defaultKey !== null) {
            storeValue('local', defaultKey, characterId);
        }
    }

    store.set(characterId);
}

const selectAll = (characterId: string | null): string | null => characterId;

/**
 * Hook for managing the currently selected character.
 *
 * Storage strategy:
 * - sessionStorage: this tab's current selection (independent per tab, survives page refresh)
 * - localStorage: the account's default character (persists across all tabs and sessions; one per account)
 *
 * On load: uses sessionStorage if available (this tab's choice), else falls back to localStorage (default)
 * On select: updates both sessionStorage (this tab's state) and localStorage (new default)
 *
 * This allows:
 * - Multiple tabs to have different selected characters
 * - Default character to be remembered across browser sessions
 * - Crucible joins to use the correct character for each tab
 *
 * The selection is shared by every caller in this tab (see the module-level store above).
 */
export function useSelectedCharacterId(): {
    selectedCharacterId: string | null;
    setSelectedCharacterId: (characterId: string | null) => void;
} {
    const selectedCharacterId = useValueStoreSelector(getSelectionStore(), selectAll);
    return { selectedCharacterId, setSelectedCharacterId };
}
